import React from 'react';
import { useNavigate } from 'react-router';
import { 
  ArrowRight, 
  CheckCircle, 
  BarChart3, 
  Users, 
  Calendar, 
  Target,
  Shield,
  Clock,
  TrendingUp,
  FileText,
  GitBranch
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ProjectLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/25 via-white to-chart-4/10">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary/25 to-chart-4/10 bg-clip-text text-transparent">
              ProjectFlow
            </h1>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Home
              </Button>
              <Button onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary/25 via-chart-4 to-chart-5 bg-clip-text text-transparent">
            Complete Project Lifecycle Management
          </h1>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            From proposal to delivery - manage your entire project lifecycle with powerful tools for planning, 
            execution, tracking, and reporting. Built for modern teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/app')} className="text-lg px-8 py-6">
              Get Started <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/stakeholder/demo')} className="text-lg px-8 py-6">
              View Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20 bg-white rounded-3xl shadow-lg my-12">
        <h2 className="text-4xl font-bold text-center mb-12">Powerful Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Target className="w-8 h-8" />}
            title="Project Proposals"
            description="Create detailed proposals with cost assignments, resource allocation, and approval workflows."
            color="blue"
          />
          <FeatureCard
            icon={<GitBranch className="w-8 h-8" />}
            title="Task Management"
            description="Organize work with epics, stories, and subtasks. Support for multiple parent relationships."
            color="purple"
          />
          <FeatureCard
            icon={<Calendar className="w-8 h-8" />}
            title="Sprint Planning"
            description="Plan and execute sprints with velocity tracking, burndown charts, and team capacity planning."
            color="green"
          />
          <FeatureCard
            icon={<Clock className="w-8 h-8" />}
            title="Time Tracking"
            description="Log hours, track estimates vs actuals, and generate detailed time reports for billing."
            color="orange"
          />
          <FeatureCard
            icon={<Users className="w-8 h-8" />}
            title="Standup Notes"
            description="Daily standup updates with blockers tracking and team visibility across date ranges."
            color="pink"
          />
          <FeatureCard
            icon={<BarChart3 className="w-8 h-8" />}
            title="Advanced Reporting"
            description="Comprehensive dashboards with performance metrics, developer ratings, and progress tracking."
            color="indigo"
          />
          <FeatureCard
            icon={<Shield className="w-8 h-8" />}
            title="Multi-Tenant Support"
            description="Complete data isolation for multiple organizations with role-based access control."
            color="red"
          />
          <FeatureCard
            icon={<FileText className="w-8 h-8" />}
            title="Bug Tracking"
            description="Dedicated bug tracker with verification links, customer reports, and resolution workflows."
            color="yellow"
          />
          <FeatureCard
            icon={<TrendingUp className="w-8 h-8" />}
            title="Retrospectives"
            description="Team retrospectives with action items, continuous improvement tracking, and insights."
            color="teal"
          />
        </div>
      </section>

      {/* Role-Based Views */}
      <section className="container mx-auto px-4 py-20">
        <h2 className="text-4xl font-bold text-center mb-12">Designed for Every Role</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <RoleCard
            title="For Developers"
            description="View assigned tasks, update status, log time, add standup notes, and track your performance."
            features={[
              'My Tasks Dashboard',
              'Time Logging',
              'Standup Notes',
              'Task Ratings',
              'Performance Metrics'
            ]}
            onClick={() => navigate('/stakeholder/developer')}
          />
          <RoleCard
            title="For Project Managers"
            description="Comprehensive project oversight with planning boards, sprint management, and team coordination."
            features={[
              'Project Planning',
              'Sprint Management',
              'Resource Allocation',
              'Progress Tracking',
              'Team Coordination'
            ]}
            onClick={() => navigate('/stakeholder/manager')}
          />
          <RoleCard
            title="For Stakeholders"
            description="High-level visibility into project status, team performance, and financial tracking."
            features={[
              'Executive Dashboard',
              'Cost Tracking',
              'Timeline Overview',
              'Performance Reports',
              'Standup Summaries'
            ]}
            onClick={() => navigate('/stakeholder/executive')}
          />
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-primary/25 to-chart-4/10 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">287</div>
              <div className="text-xl opacity-90">Active Tasks</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">12</div>
              <div className="text-xl opacity-90">Sprints Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">98%</div>
              <div className="text-xl opacity-90">On-Time Delivery</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">6</div>
              <div className="text-xl opacity-90">Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="container mx-auto px-4 py-20">
        <h2 className="text-4xl font-bold text-center mb-12">Built with Modern Technology</h2>
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <TechBadge name="React" />
            <TechBadge name="TypeScript" />
            <TechBadge name="Firebase" />
            <TechBadge name="Tailwind CSS" />
          </div>
          <p className="text-center text-muted-foreground mt-8">
            Secure, scalable, and performant architecture designed for enterprise needs
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-primary/25 to-chart-4/10 rounded-3xl p-12 text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Project Management?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join teams who trust ProjectFlow for their complete project lifecycle management
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/app')}
            className="bg-white text-primary hover:bg-muted/40 text-lg px-8 py-6"
          >
            Start Your Free Trial <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted/40 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">ProjectFlow</h3>
              <p className="text-muted-foreground">Complete project lifecycle management for modern teams</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Company</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Resources</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">Support</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-muted-foreground">
            <p>© 2026 ProjectFlow by Intellesphere Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

function FeatureCard({ icon, title, description, color }: FeatureCardProps) {
  const colorClasses = {
    blue: 'bg-primary/10 text-primary',
    purple: 'bg-chart-4/10 text-chart-4',
    green: 'bg-chart-2/10 text-chart-2',
    orange: 'bg-chart-5/10 text-chart-5',
    pink: 'bg-chart-4/10 text-chart-4',
    indigo: 'bg-chart-4/10 text-chart-4',
    red: 'bg-destructive/10 text-destructive',
    yellow: 'bg-chart-5/10 text-chart-5',
    teal: 'bg-chart-2/10 text-chart-2',
  };

  return (
    <div className="p-6 rounded-xl border border-border hover:shadow-lg transition-shadow">
      <div className={`w-16 h-16 rounded-lg ${colorClasses[color as keyof typeof colorClasses]} flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
}

interface RoleCardProps {
  title: string;
  description: string;
  features: string[];
  onClick: () => void;
}

function RoleCard({ title, description, features, onClick }: RoleCardProps) {
  return (
    <div className="bg-white p-6 rounded-xl border border-border hover:shadow-xl transition-all cursor-pointer" onClick={onClick}>
      <h3 className="text-2xl font-bold mb-3">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      <ul className="space-y-2 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center text-sm text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-chart-2 mr-2 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      <Button variant="outline" className="w-full">
        Learn More <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function TechBadge({ name }: { name: string }) {
  return (
    <div className="bg-muted/40 rounded-lg py-4 px-6 font-semibold text-muted-foreground hover:bg-muted/40 transition-colors">
      {name}
    </div>
  );
}
